import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TCreateSecretFormProps } from '@/console/features/vaults/components/create-secret/create-secret-form.types';
import { TCreateSecretInputSchema } from '@/console/features/vaults/components/create-secret/create-secret.types';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import {
  FormContainer,
  FormContent,
} from '@/shared/components/ui/form/form.styled';
import { SecretTypeEnum } from '@/shared/types/types.generated';
import { getEnumValues } from '@/shared/utils/enum/get-enum-options';

export const CreateSecretForm: React.FC<TCreateSecretFormProps> = ({
  handleClose,
  updateSecretMode,
  acceptedSecretTypes,
}) => {
  const { t } = useTranslation();
  const { control, resetField } = useFormContext();
  const secretType = useWatch({ control, name: 'secretType' });

  const secretTypeOptions = () => {
    const secretTypes = acceptedSecretTypes ?? getEnumValues(SecretTypeEnum);

    return secretTypes
      .map((type) => ({
        value: type,
        label: t(`vault:secrets.type.${type.toLowerCase()}`),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const onSecretTypeChange = (value: SecretTypeEnum) => {
    if (value === SecretTypeEnum.Certificate) {
      resetField('username');
    }
  };

  const getSecretValueLabel = () => {
    if (secretType === SecretTypeEnum.Credential) {
      return t('common:password');
    }

    return t(`vault:secrets.type.${secretType.toLowerCase()}`);
  };

  return (
    <FormContainer justifyContent="space-between">
      <FormContent overflow="auto">
        <FormInput<TCreateSecretInputSchema>
          name="name"
          label={t('vault:secrets.name')}
          required
        />
        <FormSelect<TCreateSecretInputSchema>
          name="secretType"
          label={t('vault:secrets.secret_type')}
          options={secretTypeOptions()}
          required
          onSelectChange={(value) =>
            onSecretTypeChange(value as SecretTypeEnum)
          }
          disabled={updateSecretMode || acceptedSecretTypes?.length === 1}
        />
        {secretType !== SecretTypeEnum.Certificate && (
          <FormInput<TCreateSecretInputSchema>
            name="username"
            label={t('common:username')}
            hint={t('vault:secrets.username_hint')}
          />
        )}
        <FormInput<TCreateSecretInputSchema>
          name="secretValue"
          type="password"
          label={getSecretValueLabel()}
          required
        />
      </FormContent>
      <Submit
        onClose={(event) => {
          event.stopPropagation();
          handleClose();
        }}
      />
    </FormContainer>
  );
};
